import React, { useEffect, useRef, useState } from 'react';
import { ExternalLink, Github, ArrowUpRight } from 'lucide-react';
import { projects } from '../data/mock';
import { Badge } from './ui/badge';

const Projects = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredProject, setHoveredProject] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="projects"
      ref={sectionRef}
      className="py-32 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="overflow-hidden">
            <p
              className={`text-gray-400 text-sm uppercase tracking-widest mb-4 transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              Selected Work
            </p>
          </div>
          <div className="overflow-hidden">
            <h2
              className={`text-4xl md:text-5xl font-light text-gray-900 transition-all duration-700 delay-100 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              Projects
            </h2>
          </div>
        </div>

        {/* Featured Projects */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {projects.filter(p => p.featured).map((project, index) => (
            <div
              key={project.id}
              className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
              }`}
              style={{ transitionDelay: `${200 + index * 100}ms` }}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Image Container */}
              <div className="relative h-56 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className={`w-full h-full object-cover transition-all duration-700 ${
                    hoveredProject === project.id ? 'scale-110 grayscale-0' : 'scale-100 grayscale'
                  }`}
                />
                <div className={`absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-500 ${
                  hoveredProject === project.id ? 'opacity-100' : 'opacity-0'
                }`} />
                
                {/* Hover Icon */}
                <div className={`absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center transition-all duration-500 ${
                  hoveredProject === project.id ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
                }`}>
                  <ArrowUpRight size={20} className="text-gray-900" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <Badge variant="secondary" className="mb-3 text-xs">
                  {project.category}
                </Badge>
                <h3
                  className="text-xl font-medium text-gray-900 mb-3 group-hover:text-gray-600 transition-colors duration-300"
                  style={{ fontFamily: 'Figtree, sans-serif' }}
                >
                  {project.title}
                </h3>
                <p
                  className="text-gray-500 text-sm mb-4 line-clamp-2"
                  style={{ fontFamily: 'Figtree, sans-serif' }}
                >
                  {project.description}
                </p>

                {/* Tech Stack */}
                <div className="flex flex-wrap gap-2">
                  {project.technologies.slice(0, 3).map((tech) => (
                    <span
                      key={tech}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                      style={{ fontFamily: 'JetBrains Mono, monospace' }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Other Projects */}
        <div className="grid md:grid-cols-2 gap-6">
          {projects.filter(p => !p.featured).map((project, index) => (
            <div
              key={project.id}
              className={`group bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all duration-500 hover:-translate-y-1 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
              }`}
              style={{ transitionDelay: `${500 + index * 100}ms` }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Badge variant="outline" className="mb-3 text-xs">
                    {project.category}
                  </Badge>
                  <h3
                    className="text-lg font-medium text-gray-900 mb-2 group-hover:text-gray-600 transition-colors duration-300"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    {project.title}
                  </h3>
                  <p
                    className="text-gray-500 text-sm mb-4"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                        style={{ fontFamily: 'JetBrains Mono, monospace' }}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="ml-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ArrowUpRight size={20} className="text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
